import { getDisputes } from "@services/disputesApi";
import { getCurrentMovementService, getPastMovementServices } from "@services/movementServicesApi";
import { getPortServices } from "@services/portServicesApi";
import { getRefernceDataByType, REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { FromToLocation } from "@services/vessel-call/types";
import { useQuery } from "@tanstack/react-query";
import {
  evaluateDepartureAvailability,
  unavailableDepartureAvailability,
  type DepartureAvailability,
} from "../availability/departureAvailability";

const SERVICE = "bff-vessel-call";
const PAGE_SIZE = 100;

const getAllPastMovements = async (vesselCallId: string, vesselId: string) => {
  const firstPage = await getPastMovementServices({
    service: SERVICE,
    vesselCallId,
    vesselId,
    page: 0,
    size: PAGE_SIZE,
  });
  const pageCount = Math.ceil(firstPage.totalElements / PAGE_SIZE);
  if (pageCount <= 1) return firstPage.items;

  const remainingPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) =>
      getPastMovementServices({
        service: SERVICE,
        vesselCallId,
        vesselId,
        page: index + 1,
        size: PAGE_SIZE,
      }),
    ),
  );
  return [...firstPage.items, ...remainingPages.flatMap((page) => page.items)];
};

const getAllPortServices = async (vesselCallId: string) => {
  const firstPage = await getPortServices({
    service: SERVICE,
    page: 0,
    pageSize: PAGE_SIZE,
    filters: { vesselCallId },
  });
  const pageCount = Math.ceil(firstPage.totalElements / PAGE_SIZE);
  if (pageCount <= 1) return firstPage.items;

  const remainingPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) =>
      getPortServices({
        service: SERVICE,
        page: index + 1,
        pageSize: PAGE_SIZE,
        filters: { vesselCallId },
      }),
    ),
  );
  return [...firstPage.items, ...remainingPages.flatMap((page) => page.items)];
};

const getAllDisputes = async (vesselCallId: string) => {
  const firstPage = await getDisputes({
    service: SERVICE,
    payload: { vesselCallId },
    page: 0,
    pageSize: PAGE_SIZE,
  });
  if (firstPage.totalPages <= 1) return firstPage.items;

  const remainingPages = await Promise.all(
    Array.from({ length: firstPage.totalPages - 1 }, (_, index) =>
      getDisputes({
        service: SERVICE,
        payload: { vesselCallId },
        page: index + 1,
        pageSize: PAGE_SIZE,
      }),
    ),
  );
  return [...firstPage.items, ...remainingPages.flatMap((page) => page.items)];
};

export const loadDepartureAvailability = async (vesselCallId: string, vesselId: string) => {
  const [currentMovements, pastMovements, portServices, disputes, locations] = await Promise.all([
    getCurrentMovementService({ service: SERVICE, vesselCallId, vesselId }),
    getAllPastMovements(vesselCallId, vesselId),
    getAllPortServices(vesselCallId),
    getAllDisputes(vesselCallId),
    getRefernceDataByType(REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS, SERVICE) as Promise<
      FromToLocation[]
    >,
  ]);

  return evaluateDepartureAvailability({
    currentMovements,
    pastMovements,
    portServices,
    disputes,
    locations: Array.isArray(locations) ? locations : Object.values(locations ?? {}),
  });
};

export const useDepartureAvailability = (
  vesselCallId?: string,
  vesselId?: string,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const query = useQuery<DepartureAvailability>({
    queryKey: ["departure-availability", vesselCallId, vesselId],
    queryFn: () => loadDepartureAvailability(vesselCallId as string, vesselId as string),
    enabled: enabled && Boolean(vesselCallId && vesselId),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  return {
    availability: !enabled
      ? undefined
      : (query.data ??
        (query.isError || (!query.isLoading && Boolean(vesselCallId && vesselId))
          ? unavailableDepartureAvailability()
          : undefined)),
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
};
